// fileUrlHelper.js — /files/* endpoint'leri için güvenli URL yardımcıları
// Kullanım: import { getFileUrl } from '../utils/fileUrlHelper';
import { apiRequest, ensureAccessToken, getAccessToken } from "../services/apiClient";

const API_BASE = (
  process.env.REACT_APP_API_BASE_URL || "https://api.aileyonetimi.com"
).replace(/\/+$/, "");

function getToken() {
  return getAccessToken();
}

const signedUrlRenewals = new Map();

function isHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || ""));
}

function absoluteFileUrl(url) {
  if (!url || typeof url !== "string") return url || "";

  const clean = url.trim();
  if (!clean) return "";

  // Backend'in döndürdüğü /files/... gibi relative path'ler Capacitor içinde
  // frontend origin'e düşmesin; doğrudan API domain'ine sabitlenir.
  if (clean.startsWith("/files/")) return `${API_BASE}${clean}`;

  // files/... şeklinde geldiyse de backend API domain'ine tamamla.
  if (clean.startsWith("files/")) return `${API_BASE}/${clean}`;

  return clean;
}

function signedFileVariant(url) {
  const value = String(url || "");
  const match = value.match(/\/files\/(view|preview|stream)\/([^/?#]+)(\/[^?#]*)?/);
  return match ? { kind: match[1], token: match[2], suffix: match[3] || "" } : null;
}

export function preserveSignedFileVariant(originalUrl, freshViewUrl) {
  const original = absoluteFileUrl(originalUrl);
  const fresh = absoluteFileUrl(freshViewUrl);
  const originalVariant = signedFileVariant(original);
  const freshVariant = signedFileVariant(fresh);
  if (!originalVariant || !freshVariant) return fresh;
  if (originalVariant.kind === "view") return fresh;

  try {
    const parsed = new URL(fresh);
    return `${parsed.origin}/files/${originalVariant.kind}/${freshVariant.token}${originalVariant.suffix}`;
  } catch {
    return fresh;
  }
}

export async function renewSignedFileUrl(url) {
  const original = absoluteFileUrl(url);
  if (!signedFileVariant(original)) return original;
  if (signedUrlRenewals.has(original)) return signedUrlRenewals.get(original);

  const renewal = (async () => {
    const data = await apiRequest("/files/signed-url", {
      method: "POST",
      body: JSON.stringify({ url: original }),
      skipAndroidLiteHeader: true,
    });
    if (!data?.url) throw new Error("file_url_renew_empty");
    return preserveSignedFileVariant(original, data.url);
  })();
  signedUrlRenewals.set(original, renewal);
  try {
    return await renewal;
  } finally {
    signedUrlRenewals.delete(original);
  }
}

export function getFileUrl(url) {
  const baseUrl = absoluteFileUrl(url);
  if (!baseUrl || typeof baseUrl !== "string") return baseUrl || "";
  if (baseUrl.includes("/files/")) {
    try {
      const parsed = new URL(baseUrl);
      parsed.searchParams.delete("token");
      parsed.searchParams.delete("accessToken");
      return parsed.toString();
    } catch {}
  }
  return baseUrl;
}

export function getFileFetchUrl(url) {
  return getFileUrl(url);
}

export async function fetchProtectedFile(url, options = {}) {
  const fileUrl = getFileFetchUrl(url);
  if (!fileUrl) throw new Error("File URL not found");
  const isSignedUrl = Boolean(signedFileVariant(fileUrl));
  if (!isSignedUrl) await ensureAccessToken({ source: "protected_file" });
  const token = getToken();
  const request = (requestUrl) => fetch(requestUrl, {
    credentials: "include",
    ...options,
    headers: {
      ...(!isSignedUrl && token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  let response = await request(fileUrl);
  if (isSignedUrl && (response.status === 401 || response.status === 404)) {
    const renewedUrl = await renewSignedFileUrl(fileUrl);
    if (renewedUrl && renewedUrl !== fileUrl) response = await request(renewedUrl);
  }
  return response;
}

function safeFileNameForUrl(fileName, fallback = "video.mp4") {
  const raw = String(fileName || fallback || "video.mp4").trim() || "video.mp4";
  const cleaned = raw
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^\.+/, "")
    .slice(0, 120);
  return cleaned || fallback || "video.mp4";
}

export function getVideoPlaybackUrl(url, fileName = "video.mp4") {
  const baseUrl = absoluteFileUrl(url);
  if (!baseUrl || typeof baseUrl !== "string") return baseUrl || "";

  // Safari/PWA video oynatıcıları tokenlı URL'de dosya uzantısı olmadığında
  // metadata/thumbnail ve Range isteklerinde sorun çıkarabiliyor. Backend'de aynı
  // tokenı çözen, sonu .mp4/.mov gibi gerçek dosya adıyla biten stream endpoint'i kullanılır.
  const match = baseUrl.match(/\/files\/view\/([^/?#]+)/);
  if (match && match[1]) {
    const origin = baseUrl.slice(0, baseUrl.indexOf("/files/view/"));
    return `${origin}/files/stream/${encodeURIComponent(match[1])}/${encodeURIComponent(safeFileNameForUrl(fileName, "video.mp4"))}`;
  }

  return getFileUrl(url);
}

export function getPublicFileUrl(url) {
  const baseUrl = absoluteFileUrl(url);
  if (!baseUrl || !isHttpUrl(baseUrl)) return baseUrl || "";
  return baseUrl;
}
